import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useT } from '../i18n/i18n';

type Props = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  /** Do the work. Throw with a message to show it in the dialog and stay open. */
  onConfirm: (password: string) => Promise<void>;
  onCancel: () => void;
};

export default function PasswordDialog({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: Props) {
  const t = useT();
  const titleId = useId();
  const descId = useId();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setError(null);
    setBusy(false);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('That did not work. Try again.'));
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <h2 id={titleId} className="modal-title">{title}</h2>
        <p id={descId} className="modal-body">{message}</p>
        <form onSubmit={submit} className="spaced">
          <div className="field">
            <label htmlFor="confirm-password-input" className="field-hint">{t('Your password')}</label>
            <input
              id="confirm-password-input"
              ref={inputRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>

          {error && <div className="error" role="alert">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn secondary" onClick={onCancel} disabled={busy}>
              {t('Cancel')}
            </button>
            <button type="submit" className="btn" disabled={busy || !password}>
              {busy ? t('Checking…') : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
